import React, { useState } from 'react'
import { Box } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import PageTitle from '../../../components/PageTitle'
import StageFooter from '../../merchants/tat-wizard/components/StageFooter'
import { SX } from '../../merchants/shared/tokens'
import { DONE_HELP, DONE_STEP, ONBOARDING_STEPS, ROUTES } from '../constants'
import { merchantLabel } from '../components/SetupUi'
import KpiStrip from './KpiStrip'
import PtlTopBar from './PtlTopBar'
import PtlStepper from './PtlStepper'
import PtlStageHeader from './PtlStageHeader'
import PtlHelpDrawer from './PtlHelpDrawer'

/**
 * Frame of one PTL onboarding step, the D2C onboarding layout: sticky top bar → sticky stepper → "Stage n of N" header
 * → KPI cards → one body card → sticky footer (Back / Next, with the reason when Next is blocked). The Done screen keeps
 * the bar and stepper but has no stage header or footer, as in D2C.
 *
 * @param mode    'onboard' (from Onboarding Health) or 'update' (from Merchant Update; same screens, all steps open)
 * @param states  step states from MerchantStatus.stepStates
 * @param footer  { onBack, onNext, nextLabel, nextDisabled, hint }
 * @param guard   runs a navigation away from the step (top bar link); the step page uses it to ask about unsaved work
 */
export default function PtlWizardShell({ mode = 'onboard', merchant, stepKey, states, onGoStep, guard = (fn) => fn(), kpis, kpisLoading, footer, children }) {
  const navigate = useNavigate()
  const [helpOpen, setHelpOpen] = useState(false)
  const isDone = stepKey === DONE_STEP
  const index = isDone ? ONBOARDING_STEPS.length : ONBOARDING_STEPS.findIndex((s) => s.key === stepKey)
  const step = ONBOARDING_STEPS[index]
  const name = merchantLabel(merchant)
  const update = mode === 'update'
  const current = isDone ? 'Done' : `Step ${index + 1} · ${step.label}`
  const parent = update
    ? { label: merchant?.name || 'Merchant Update', to: merchant ? ROUTES.updateHub(merchant.id) : ROUTES.update }
    : { label: 'Onboarding Health', to: ROUTES.onboarding }

  return (
    <>
      <PageTitle>{`${update ? 'PTL Merchant Update' : 'PTL Onboarding'} · ${current}${merchant ? ` · ${merchant.name}` : ''}`}</PageTitle>
      <Box>
        <PtlTopBar parent={parent} onParent={() => guard(() => navigate(parent.to))}
          current={current} merchantName={name} modeLabel={update ? 'Update mode' : null} onHelp={() => setHelpOpen(true)} />
        <PtlStepper steps={ONBOARDING_STEPS} current={stepKey} states={states} onGo={onGoStep} />
        <Box sx={{ maxWidth: 1640, mx: 'auto', px: 2.5, pt: 2.25, pb: 4, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
          {!isDone && <PtlStageHeader index={index} total={ONBOARDING_STEPS.length} title={step.title} explainer={step.explainer} />}
          {kpis && <Box sx={{ '& > [role=group]': { mb: 0 } }}><KpiStrip items={kpis} loading={kpisLoading} ariaLabel={`${current} key figures`} /></Box>}
          <Box sx={{ ...SX.card, p: 2.5 }}>{children}</Box>
          {!isDone && footer && (
            <StageFooter stage={index} totalStages={ONBOARDING_STEPS.length + 1} onBack={footer.onBack} onNext={footer.onNext}
              nextLabel={footer.nextLabel} nextDisabled={footer.nextDisabled} hint={footer.hint} />
          )}
        </Box>
      </Box>
      <PtlHelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} step={isDone ? DONE_HELP : step} stepIndex={index} total={ONBOARDING_STEPS.length} />
    </>
  )
}
